/**
 * 消費詳細の表の列（プロダクト仕様書「月ごとの推移」「ユーザーごとの内訳」「アカウントごとの内訳」「機能ごとの内訳」）。
 */

import type { ReactNode } from 'react';

import { planLabels } from '@/components/credit-contracts/display';
import type {
  AccountRow,
  CategoryColumns,
  FeatureRow,
  MonthDetail,
  UserRow,
} from '@/lib/api/credit-consumption';
import { monthLabel } from '@/lib/credit-contracts/month';

import { breakdownColumns, credit, integer, NONE, percent, terabytes } from './display';
import { FeatureLabel } from './FeatureLabel';
import type { HeaderColumn } from './GroupedHeader';
import type { SortValue } from './sorting';

/** 列を描画するときの行の状態。行の下に行を並べる表（機能ごとの内訳）で使う */
export interface RowContext {
  /** 上の行は0、その下に並べる行は1 */
  depth: number;
  /** 下に並べる行を表示しているか。下に並べる行がなければ null */
  expanded: boolean | null;
  toggle: () => void;
}

export interface DetailColumn<T> {
  header: HeaderColumn;
  sortValue: (row: T) => SortValue;
  render: (row: T, context: RowContext) => ReactNode;
  /** この列の見出しに、下の行をまとめて開け閉めする印を置く（行の下に行を並べる表だけ） */
  expandAll?: boolean;
}

const dec = (value: string | null): SortValue => ({ kind: 'decimal', value });

function column<T>(
  key: string,
  path: string[],
  unit: string | undefined,
  numeric: boolean,
  sortValue: (row: T) => SortValue,
  render: (row: T, context: RowContext) => ReactNode,
): DetailColumn<T> {
  return { header: { key, path, unit, numeric, sortable: true }, sortValue, render };
}

/** 消費の内訳の列（表示区分の見出しの下に消費項目）。値は行の items と category_totals から */
function breakdown<T>(
  categories: CategoryColumns[],
  values: (
    row: T,
  ) => { items: Record<string, string>; category_totals: Record<string, string> } | null,
): DetailColumn<T>[] {
  return breakdownColumns(categories).map((c) => {
    const value = (row: T) => {
      const v = values(row);
      return v ? c.value(v.items, v.category_totals) : null;
    };
    return column<T>(
      c.key,
      c.group ? [c.group, c.label] : [c.label],
      '（クレジット）',
      true,
      (r) => dec(value(r)),
      (r) => credit(value(r)),
    );
  });
}

/** 月ごとの推移 */
export function monthColumns(categories: CategoryColumns[]): DetailColumn<MonthDetail>[] {
  return [
    column<MonthDetail>(
      'month',
      ['月'],
      undefined,
      false,
      (r) => ({ kind: 'code', value: r.month }),
      (r) => monthLabel(r.month),
    ),
    column<MonthDetail>(
      'plan',
      ['料金プラン'],
      undefined,
      false,
      (r) => ({ kind: 'text', value: r.contract.pricing_plan }),
      (r) => (r.contract.pricing_plan ? planLabels[r.contract.pricing_plan] : 'なし'),
    ),
    column<MonthDetail>(
      'contract_credit',
      ['契約クレジット数'],
      '（クレジット/月）',
      true,
      (r) => dec(r.contract.contract_credit),
      (r) => credit(r.contract.contract_credit),
    ),
    column<MonthDetail>(
      'monthly_fee',
      ['月額料金'],
      '（円）',
      true,
      (r) => dec(r.contract.monthly_fee),
      (r) => integer(r.contract.monthly_fee),
    ),
    column<MonthDetail>(
      'store_sku',
      ['店舗×SKU'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_sku),
      (r) => integer(r.snapshots.store_sku),
    ),
    column<MonthDetail>(
      's3_tb',
      ['S3容量'],
      '（TB）',
      true,
      (r) => dec(r.snapshots.s3_tb),
      (r) => terabytes(r.snapshots.s3_tb),
    ),
    column<MonthDetail>(
      'user_count',
      ['ユーザー数'],
      '（人）',
      true,
      (r) => dec(r.snapshots.user_count),
      (r) => integer(r.snapshots.user_count),
    ),
    column<MonthDetail>(
      'store_agent_store_sellers',
      ['ストアエージェント(店舗向け)の店舗数'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_agent_store_sellers),
      (r) => integer(r.snapshots.store_agent_store_sellers),
    ),
    column<MonthDetail>(
      'store_agent_hub_sellers',
      ['ストアエージェント(本部向け)の店舗数'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_agent_hub_sellers),
      (r) => integer(r.snapshots.store_agent_hub_sellers),
    ),
    column<MonthDetail>(
      'data_connect',
      ['データコネクト'],
      '（クレジット/月）',
      true,
      (r) => dec(r.data_connect),
      (r) => credit(r.data_connect),
    ),
    ...breakdown<MonthDetail>(categories, (r) => r.consumption),
    column<MonthDetail>(
      'consumption',
      ['消費クレジット数'],
      '（クレジット）',
      true,
      (r) => dec(r.consumption?.total ?? null),
      (r) => credit(r.consumption?.total),
    ),
    column<MonthDetail>(
      'ratio',
      ['契約に対する割合'],
      '（%）',
      true,
      (r) => dec(r.ratio),
      (r) => percent(r.ratio),
    ),
  ];
}

/** 氏名。削除済みのユーザー・氏名が未登録のストアエージェントのユーザー・ユーザーに紐付かない消費の行は、それぞれの表示 */
export function userName(r: UserRow): string {
  if (r.no_user) return 'ユーザーに紐付かない消費';
  if (r.deleted) return '削除ユーザー';
  return r.name ?? NONE;
}

/** ユーザーごとの内訳 */
export function userColumns(categories: CategoryColumns[]): DetailColumn<UserRow>[] {
  return [
    column<UserRow>(
      'name',
      ['氏名'],
      undefined,
      false,
      (r) => ({ kind: 'text', value: userName(r) }),
      userName,
    ),
    column<UserRow>(
      'email',
      ['メールアドレス'],
      undefined,
      false,
      (r) => ({ kind: 'code', value: r.email }),
      (r) => r.email ?? NONE,
    ),
    ...breakdown<UserRow>(categories, (r) => r),
    column<UserRow>(
      'total',
      ['合計'],
      '（クレジット）',
      true,
      (r) => dec(r.total),
      (r) => credit(r.total),
    ),
    column<UserRow>(
      'share',
      ['構成比'],
      '（%）',
      true,
      (r) => dec(r.share),
      (r) => percent(r.share),
    ),
  ];
}

/** アカウントごとの内訳 */
export function accountColumns(categories: CategoryColumns[]): DetailColumn<AccountRow>[] {
  return [
    column<AccountRow>(
      'code',
      ['アカウントコード'],
      undefined,
      false,
      (r) => ({ kind: 'code', value: r.code }),
      (r) => r.code ?? NONE,
    ),
    column<AccountRow>(
      'name',
      ['アカウント名'],
      undefined,
      false,
      (r) => ({ kind: 'text', value: r.name }),
      (r) => r.name ?? '削除済み',
    ),
    column<AccountRow>(
      'store_sku',
      ['店舗×SKU'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_sku),
      (r) => integer(r.snapshots.store_sku),
    ),
    column<AccountRow>(
      's3_tb',
      ['S3容量'],
      '（TB）',
      true,
      (r) => dec(r.snapshots.s3_tb),
      (r) => terabytes(r.snapshots.s3_tb),
    ),
    column<AccountRow>(
      'store_agent_store_sellers',
      ['ストアエージェント(店舗向け)の店舗数'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_agent_store_sellers),
      (r) => integer(r.snapshots.store_agent_store_sellers),
    ),
    column<AccountRow>(
      'store_agent_hub_sellers',
      ['ストアエージェント(本部向け)の店舗数'],
      undefined,
      true,
      (r) => dec(r.snapshots.store_agent_hub_sellers),
      (r) => integer(r.snapshots.store_agent_hub_sellers),
    ),
    ...breakdown<AccountRow>(categories, (r) => r),
    column<AccountRow>(
      'total',
      ['実行消費の合計'],
      '（クレジット）',
      true,
      (r) => dec(r.total),
      (r) => credit(r.total),
    ),
  ];
}

/** 「機能」の列の表示。機能のテーブルにない機能コードの機能の行は null（「-」と表示する） */
function featureName(r: FeatureRow): string | null {
  switch (r.kind) {
    case 'Category':
      return r.feature_category_name;
    case 'Unclassified':
      return '未分類';
    case 'NoFeatureCode':
      return '機能コードなし';
    case 'Feature':
      return r.feature_name;
  }
}

/** 機能ごとの内訳 */
export function featureColumns(categories: CategoryColumns[]): DetailColumn<FeatureRow>[] {
  return [
    column<FeatureRow>(
      'account_code',
      ['アカウントコード'],
      undefined,
      false,
      (r) => ({ kind: 'code', value: r.account_code }),
      (r) => r.account_code ?? NONE,
    ),
    column<FeatureRow>(
      'account_name',
      ['アカウント名'],
      undefined,
      false,
      (r) => ({ kind: 'text', value: r.account_name }),
      (r) => r.account_name ?? '削除済み',
    ),
    {
      ...column<FeatureRow>(
        'feature',
        ['機能'],
        undefined,
        false,
        (r) => ({ kind: 'text', value: featureName(r) }),
        (r, context) => <FeatureLabel label={featureName(r) ?? NONE} context={context} />,
      ),
      expandAll: true,
    },
    column<FeatureRow>(
      'feature_code',
      ['機能コード'],
      undefined,
      false,
      (r) => ({ kind: 'code', value: r.feature_code }),
      (r) => r.feature_code ?? NONE,
    ),
    ...breakdown<FeatureRow>(categories, (r) => r),
    column<FeatureRow>(
      'total',
      ['合計'],
      '（クレジット）',
      true,
      (r) => dec(r.total),
      (r) => credit(r.total),
    ),
    column<FeatureRow>(
      'share',
      ['構成比'],
      '（%）',
      true,
      (r) => dec(r.share),
      (r) => percent(r.share),
    ),
  ];
}
